import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { BaseModel } from '../../../shared/src/lib/shared/models/entity/base/base.model';
import {
  createMockDatabase,
  MockDatabase,
  MockEntityStore,
  MockPage,
  MockQuery,
} from './mock-entity.store';
import { ENVIRONMENT } from '@shared/tokens/environment.token';
import { ApiResponse } from '@shared/types/api-response.type';
import { QueryPagination } from '@shared/types/query-options.types';

// -----------------------------------------------------------------------------------------------------
// @ CONSTANTS
// -----------------------------------------------------------------------------------------------------

/** The one and only instance of the mock database — import it in tests to seed or reset a collection. */
export const MOCK_DB: MockDatabase = createMockDatabase();

/** Simulated network latency, so loading states are actually visible while developing. */
const LATENCY_MS: number = 250;

/** The default page size, when the caller paginates without saying how large a page is. */
const DEFAULT_PAGE_SIZE: number = 20;

/** Query params that steer the query rather than filter it — everything else is a filter key. */
const RESERVED_PARAMS: readonly string[] = ['page', 'pageSize', 'take', 'skip', 'orderby', 'count'];

/**
 * Maps the last path segment of an endpoint to its store.
 * Keep the keys in sync with `ENDPOINTS.*.base` — that's the only coupling to the real services.
 */
const ROUTES: Record<string, MockEntityStore<BaseModel>> = {
  properties: MOCK_DB.properties as MockEntityStore<BaseModel>,
  buildings: MOCK_DB.buildings as MockEntityStore<BaseModel>,
  units: MOCK_DB.units as MockEntityStore<BaseModel>,
  contacts: MOCK_DB.contacts as MockEntityStore<BaseModel>,
  files: MOCK_DB.files as MockEntityStore<BaseModel>,
  channels: MOCK_DB.channels as MockEntityStore<BaseModel>,
};

// -----------------------------------------------------------------------------------------------------
// @ INTERCEPTOR
// -----------------------------------------------------------------------------------------------------

/**
 * Serves the mock database over HTTP, so `EntityService` and the adapters run exactly as they will
 * against the real backend — same options bag, same envelope, same mapping. When the API is ready,
 * drop this one provider and nothing else changes.
 *
 * Register it in `app.config.ts`, behind an environment flag:
 *
 * ```ts
 * provideHttpClient(withInterceptors(environment.useMockApi ? [mockApiInterceptor] : []))
 * ```
 *
 * Requests to the identity provider are never mocked — auth, token and user endpoints always go to
 * the real IdP server, even though they travel through the same `HttpClient`.
 *
 * @param request - The outgoing request.
 * @param next - The next handler, used for anything this interceptor doesn't own.
 * @returns {Observable<HttpEvent<unknown>>} The mocked response.
 */
export const mockApiInterceptor: HttpInterceptorFn = (request, next) => {
  const idpServerUrl: string = inject(ENVIRONMENT).idpServerUrl;

  if (isIdpRequest(request, idpServerUrl)) {
    return next(request);
  }

  const route = resolveRoute(request);

  if (route === null) {
    return next(request);
  }

  const { store, id } = route;

  switch (request.method) {
    case 'GET': {
      return id === null ? respondWithPage(list(store, request)) : respondOrFail(store.getById(id));
    }

    case 'POST': {
      return respond(store.create(request.body as Partial<BaseModel>), { statusCode: 201 });
    }

    case 'PATCH':
    case 'PUT': {
      return respondOrFail(store.update(id ?? '', request.body as Partial<BaseModel>));
    }

    case 'DELETE': {
      return store.deleteById(id ?? '') ? respond(null, { statusCode: 204 }) : notFound();
    }

    default: {
      return next(request);
    }
  }
};

// -----------------------------------------------------------------------------------------------------
// @ PRIVATE
// -----------------------------------------------------------------------------------------------------

/**
 * Whether the request is bound for the identity provider.
 *
 * This has to be checked before the route matcher runs: the IdP exposes paths like
 * `{idpServerUrl}/api/v1/users`, which the segment matcher would otherwise happily claim — and a
 * mocked login is worse than no login at all. Compared on the origin, so a path that merely looks
 * similar on the app's own host is still mocked.
 *
 * @param request - The outgoing request.
 * @param idpServerUrl - The IdP base url, from `inject(ENVIRONMENT).idpServerUrl`.
 * @returns {boolean} True when the request must bypass the mock backend.
 */
function isIdpRequest(request: HttpRequest<unknown>, idpServerUrl: string): boolean {
  if (!idpServerUrl) {
    return false;
  }

  // A relative url can never reach the IdP — it is resolved against the app origin.
  if (!/^https?:\/\//i.test(request.url)) {
    return false;
  }

  const target: URL = new URL(request.url);
  const idp: URL = new URL(idpServerUrl);

  if (target.origin.toLowerCase() !== idp.origin.toLowerCase()) {
    return false;
  }

  const idpPath: string = idp.pathname.replace(/\/+$/, '');

  return idpPath === '' || target.pathname.toLowerCase().startsWith(idpPath.toLowerCase());
}

/**
 * Resolves the store and the optional id from the request url.
 *
 * @param request - The outgoing request.
 * @returns The matched store and id, or null when the url isn't mocked.
 */
function resolveRoute(
  request: HttpRequest<unknown>,
): { store: MockEntityStore<BaseModel>; id: string | null } | null {
  const segments: string[] = new URL(request.url, 'http://mock').pathname
    .split('/')
    .filter(Boolean);

  for (let index = segments.length - 1; index >= 0; index--) {
    const store: MockEntityStore<BaseModel> | undefined = ROUTES[segments[index]];

    if (store) {
      return { store, id: segments[index + 1] ?? null };
    }
  }

  return null;
}

/**
 * Runs a list request against a store, translating the query params into a store query.
 *
 * Everything that isn't a reserved param is treated as a filter key, so a `PropertyFilter` field
 * lands on the matching model field without the interceptor knowing anything about the entity.
 *
 * @param store - The store to query.
 * @param request - The outgoing request.
 * @returns {MockPage<BaseModel>} The matching page, and the total before pagination.
 */
function list(
  store: MockEntityStore<BaseModel>,
  request: HttpRequest<unknown>,
): MockPage<BaseModel> {
  const params = request.params;

  const query: MockQuery<BaseModel> = {
    filter: Object.fromEntries(
      params
        .keys()
        .filter((key: string) => !RESERVED_PARAMS.includes(key))
        .map((key: string) => [
          key,
          params.getAll(key)?.length === 1 ? params.get(key) : params.getAll(key),
        ]),
    ),
    sorting: parseSort(params.getAll('orderby') ?? []),
    pagination: parsePagination(params),
  };

  return store.query(query);
}

/**
 * Parses the pagination params.
 *
 * Understands both the current shape (`page` / `pageSize`) and the `take` / `skip` shape the
 * EntityService is expected to move to, so this keeps working through that change.
 *
 * @param params - The request params.
 * @returns {QueryPagination | undefined} The pagination, or undefined when the caller wants everything.
 */
function parsePagination(params: HttpParams): QueryPagination | undefined {
  if (params.has('page')) {
    return {
      page: Number(params.get('page')),
      pageSize: Number(params.get('pageSize') ?? DEFAULT_PAGE_SIZE),
    };
  }

  if (params.has('take')) {
    const pageSize: number = Number(params.get('take'));
    const skip: number = Number(params.get('skip') ?? 0);

    return { page: Math.floor(skip / Math.max(1, pageSize)) + 1, pageSize };
  }

  return undefined;
}

/**
 * Parses the sort params. A leading `-` means descending, e.g. `-createdAt`; `name` is ascending.
 * Several sort params (or one comma-separated param) are applied in the order they arrive.
 *
 * @param values - The raw sort params.
 * @returns {MockQuery<BaseModel>['sorting']} The sort object, or undefined when nothing was requested.
 */
function parseSort(values: string[]): MockQuery<BaseModel>['sorting'] {
  const fields: string[] = values.flatMap((value: string) => value.split(',')).filter(Boolean);

  if (fields.length === 0) {
    return undefined;
  }

  const sorting: Record<string, 'asc' | 'desc'> = {};

  for (const field of fields) {
    const isDescending: boolean = field.startsWith('-');

    sorting[isDescending ? field.slice(1) : field] = isDescending ? 'desc' : 'asc';
  }

  return sorting as MockQuery<BaseModel>['sorting'];
}

/**
 * Wraps a payload in the `ApiResponse` envelope the real API returns, with simulated latency.
 *
 * This is the single place that knows the envelope: the adapters unwrap it on the way back exactly
 * as they will against the real backend.
 *
 * @param data - The payload.
 * @param options - The total (for list responses) and the status code.
 * @returns {Observable<HttpEvent<unknown>>} The mocked response.
 */
function respond<T>(
  data: T,
  options: { total?: number; statusCode?: number } = {},
): Observable<HttpEvent<unknown>> {
  const statusCode: number = options.statusCode ?? 200;

  const body: ApiResponse<T> = {
    data,
    description: '',
    message: '',
    total: options.total ?? (Array.isArray(data) ? data.length : 0),
    errors: null,
    code: { statusCode, internalCode: 0 },
  };

  return of(new HttpResponse({ status: statusCode, body })).pipe(delay(LATENCY_MS));
}

/**
 * Responds with a page: the items go in `data`, the pre-pagination count goes in `total`.
 *
 * @param page - The page returned by the store.
 * @returns {Observable<HttpEvent<unknown>>} The mocked response.
 */
function respondWithPage(page: MockPage<BaseModel>): Observable<HttpEvent<unknown>> {
  return respond(page.items, { total: page.total });
}

/**
 * Responds with the entity, or a 404 when it doesn't exist.
 *
 * @param entity - The resolved entity, or null.
 * @returns {Observable<HttpEvent<unknown>>} The mocked response.
 */
function respondOrFail(entity: BaseModel | null): Observable<HttpEvent<unknown>> {
  if (entity === null) {
    return notFound();
  }

  return respond(entity);
}

/**
 * Builds a 404 response, carrying the same envelope as a successful call.
 *
 * @returns {Observable<never>} The failing response.
 */
function notFound(): Observable<never> {
  const body: ApiResponse<null> = {
    data: null,
    description: 'The requested entity does not exist.',
    message: 'Not Found',
    total: 0,
    errors: null,
    code: { statusCode: 404, internalCode: 0 },
  };

  return throwError(
    () => new HttpErrorResponse({ status: 404, statusText: 'Not Found', error: body }),
  ).pipe(delay(LATENCY_MS));
}
